import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { message } from "antd";
import { getFastTranslateSeriesPageApi } from "../../service/fastTranslateSeries";
import { useSeriesButtonPermissions } from "../../hooks/useSeriesButtonPermissions";
import { UserRoleManager } from "../../utils/userRole";
import { globalStore } from "../../utils/globalStore";
import type { HomeSeriesFolderItem } from "./types";
import type { HomeScopeOption, HomeTimeSortOption } from "./useHomeSeriesSurface";
import { getHomeSeriesListPageSize } from "./homeSeriesGridLayout";

const getDefaultOwnerType = (): number => {
  const currentRole = UserRoleManager.getCurrentUserRole();
  if (currentRole?.isUser) {
    return 2;
  }
  return 1;
};

const mapFastRowToFolder = (row: FastTranslateSeries.FastTranslateSeriesPageItem): HomeSeriesFolderItem => {
  const id = Number(row.seriesId);
  return {
    id: Number.isFinite(id) ? id : 0,
    name: String(row.name ?? ""),
  };
};

function parsePageQueryString(pageVal: string | null): number {
  const n = parseInt(pageVal || "1", 10);
  return Math.max(1, Number.isFinite(n) ? n : 1);
}

/**
 * 一键速译外层列表：getFastTranslateSeriesPageApi；分页、防抖搜索、排序/范围与精品外层一致。
 */
export const useQuickSeriesSurface = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAdminOrSuperAdmin } = useSeriesButtonPermissions();

  const [searchInputValue, setSearchInputValue] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [timeSort, setTimeSort] = useState<HomeTimeSortOption>("time");
  const [scope, setScope] = useState<HomeScopeOption>(() =>
    isAdminOrSuperAdmin() ? "team" : getDefaultOwnerType() === 2 ? "mine" : "team"
  );

  const [page, setPage] = useState(() => parsePageQueryString(searchParams.get("page")));
  const [pageSize, setPageSize] = useState(() =>
    typeof window !== "undefined" ? getHomeSeriesListPageSize(window.innerWidth) : 15
  );

  const outerListPageQs = searchParams.get("page");
  useEffect(() => {
    const n = parsePageQueryString(outerListPageQs);
    setPage((prev) => (prev === n ? prev : n));
  }, [outerListPageQs]);

  const pageInUrl = searchParams.get("page");
  useEffect(() => {
    if (page <= 1) {
      if (!pageInUrl) return;
    } else if (pageInUrl === String(page)) {
      return;
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (page <= 1) next.delete("page");
        else next.set("page", String(page));
        return next;
      },
      { replace: true }
    );
  }, [page, pageInUrl, setSearchParams]);

  const [pageItems, setPageItems] = useState<HomeSeriesFolderItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pagination, setPagination] = useState({
    total: 0,
    pages: 0,
    has_next: false,
    has_prev: false,
  });
  const paginationRef = useRef(pagination);
  paginationRef.current = pagination;

  const resolveOwnerTypeForApi = useCallback((): number | undefined => {
    if (!isAdminOrSuperAdmin()) {
      return getDefaultOwnerType();
    }
    if (scope === "team") return 1;
    return 2;
  }, [isAdminOrSuperAdmin, scope]);

  const resolveSortForApi = useCallback((): number => {
    void timeSort;
    return 1;
  }, [timeSort]);

  const refresh = useCallback(async () => {
    try {
      setIsLoading(true);
      const ownerType = resolveOwnerTypeForApi();
      const res = await getFastTranslateSeriesPageApi({
        pageNo: page,
        pageSize,
        sort: resolveSortForApi(),
        ...(typeof ownerType === "number" ? { ownerType } : {}),
        ...(searchQuery.trim() ? { name: searchQuery.trim() } : {}),
      });

      if (res?.code !== 0) {
        message.error(res?.msg || "获取剧集列表失败");
        setPageItems([]);
        const empty = { total: 0, pages: 0, has_next: false, has_prev: false };
        setPagination(empty);
        paginationRef.current = empty;
        return;
      }

      const list = res.data?.list ?? [];
      const total = Number(res.data?.total ?? 0);
      const items = list.map(mapFastRowToFolder).filter((it) => it.id > 0);
      setPageItems(items);

      const pages = pageSize > 0 ? Math.ceil(total / pageSize) : 0;
      const nextPagination = {
        total,
        pages,
        has_next: page * pageSize < total,
        has_prev: page > 1,
      };
      setPagination(nextPagination);
      paginationRef.current = nextPagination;
    } catch {
      message.error("获取剧集列表失败");
      setPageItems([]);
      const empty = { total: 0, pages: 0, has_next: false, has_prev: false };
      setPagination(empty);
      paginationRef.current = empty;
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, searchQuery, resolveOwnerTypeForApi, resolveSortForApi]);

  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useEffect(() => {
    const userInfoFingerprint = () => {
      const u = globalStore.getUserInfo();
      if (!u) return "";
      const roles = Array.isArray(u.roles) ? u.roles : [];
      const roleKeys = roles
        .map((x: unknown) =>
          typeof x === "string"
            ? x
            : String(
                (x as { role_key?: string; code?: string })?.role_key ??
                  (x as { code?: string }).code ??
                  ""
              )
        )
        .filter(Boolean)
        .join(",");
      return `${String(u.id ?? (u as { userId?: unknown }).userId ?? "")}|${roleKeys}|${String(u.role ?? "")}`;
    };
    const roleFingerprint = () => {
      const r = UserRoleManager.getCurrentUserRole();
      if (!r) return "null";
      return `${Number(r.isAdmin)}${Number(r.isAssessor)}${Number(r.isOperator)}${Number(r.isUser)}`;
    };
    const snap = () => `${userInfoFingerprint()}#${roleFingerprint()}`;
    let prev = snap();
    return globalStore.subscribe(() => {
      const next = snap();
      if (next !== prev) {
        prev = next;
        void refreshRef.current();
      }
    });
  }, []);

  useEffect(() => {
    const updatePageSize = () => {
      const newPageSize = getHomeSeriesListPageSize(window.innerWidth);
      setPageSize((prev) => {
        if (newPageSize !== prev) {
          setPage(1);
          return newPageSize;
        }
        return prev;
      });
    };
    updatePageSize();
    window.addEventListener("resize", updatePageSize);
    return () => window.removeEventListener("resize", updatePageSize);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    void refresh();
  }, [location.pathname, refresh]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchInputValue(value);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setSearchQuery(value);
      setPage(1);
      debounceTimerRef.current = null;
    }, 500);
  }, []);

  const setTimeSortAndReset = useCallback((v: HomeTimeSortOption) => {
    setTimeSort(v);
    setPage(1);
  }, []);

  const setScopeAndReset = useCallback((v: HomeScopeOption) => {
    setScope(v);
    setPage(1);
  }, []);

  const goPrev = useCallback(() => {
    if (paginationRef.current.has_prev) {
      setPage((p) => Math.max(1, p - 1));
    }
  }, []);

  const goNext = useCallback(() => {
    if (paginationRef.current.has_next) {
      setPage((p) => p + 1);
    }
  }, []);

  const pageCount = pagination.total === 0 ? 1 : Math.max(1, pagination.pages);

  return {
    searchInputValue,
    handleSearchChange,
    timeSort,
    setTimeSort: setTimeSortAndReset,
    scope,
    setScope: setScopeAndReset,
    showScopeFilter: isAdminOrSuperAdmin(),
    page,
    pageSize,
    pageCount,
    pageItems,
    totalCount: pagination.total,
    hasPrev: pagination.has_prev,
    hasNext: pagination.has_next,
    isLoading,
    refresh,
    goPrev,
    goNext,
  };
};
